import { useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { settings } from "../../../shared/settings";
import type { FieldValue, Submission, SubmissionDetail } from "./types";

const base = () => settings.environment.VITE_PROCESS_HUB_BASE_URL ?? "";

export function useSubmission() {
  const toast = useToast();
  const { request } = useApiRequest();

  const createSubmission = useCallback(
    async (formTypeId: number): Promise<Submission> => {
      return request({
        url: `${base()}/api/v1/submissions`,
        method: "POST",
        payload: { form_type_id: formTypeId },
        skipToast: true,
      });
    },
    [request]
  );

  const getSubmission = useCallback(
    async (submissionId: number): Promise<SubmissionDetail> => {
      return request({
        url: `${base()}/api/v1/submissions/${submissionId}`,
        method: "GET",
        skipToast: true,
      });
    },
    [request]
  );

  const saveDraft = useCallback(
    async (submissionId: number, fieldValues: FieldValue[]): Promise<Submission> => {
      try {
        const result = await request({
          url: `${base()}/api/v1/submissions/${submissionId}/draft`,
          method: "PATCH",
          payload: { field_values: fieldValues },
          skipToast: true,
        });
        toast({ title: "Rascunho salvo", status: "success", duration: 4000, isClosable: true });
        return result;
      } catch (err) {
        toast({ title: "Erro ao salvar rascunho", status: "error", duration: 6000, isClosable: true });
        throw err;
      }
    },
    [request, toast]
  );

  const submit = useCallback(
    async (submissionId: number, fieldValues: FieldValue[]): Promise<Submission> => {
      try {
        const result = await request({
          url: `${base()}/api/v1/submissions/${submissionId}/submit`,
          method: "POST",
          payload: { field_values: fieldValues },
          skipToast: true,
        });
        toast({ title: "Solicitação enviada", status: "success", duration: 4000, isClosable: true });
        return result;
      } catch (err) {
        toast({ title: "Erro ao enviar solicitação", status: "error", duration: 6000, isClosable: true });
        throw err;
      }
    },
    [request, toast]
  );

  const uploadFile = useCallback(
    async (
      submissionId: number,
      fieldDefinitionId: number,
      file: File
    ): Promise<{ download_url: string; file_name: string }> => {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const result = await request({
          url: `${base()}/api/v1/submissions/${submissionId}/fields/${fieldDefinitionId}/upload`,
          method: "POST",
          payload: formData,
          skipToast: true,
        });
        return result;
      } catch (err) {
        toast({ title: "Erro ao enviar arquivo", status: "error", duration: 6000, isClosable: true });
        throw err;
      }
    },
    [request, toast]
  );

  return { createSubmission, getSubmission, saveDraft, submit, uploadFile };
}
